import { isClient } from "@tamagui/constants";
const allSelectors = {};
const allRules = {};
const insertedTransforms = {};
const getAllSelectors = () => allSelectors;
const getAllRules = () => Object.values(allRules);
const getAllTransforms = () => insertedTransforms;
function addTransform(identifier, css, rule) {
  const s = css.indexOf("transform:");
  if (s === -1) {
    if (process.env.NODE_ENV === "development") {
      console.error(`\u274C Invalid transform, likely used deg/% improperly ${identifier}`);
    }
    return;
  }
  const startI = s + "transform:".length;
  const endI = css.indexOf(";");
  const value = css.slice(startI, endI);
  if (!insertedTransforms[identifier]) {
    insertedTransforms[identifier] = value;
    return true;
  }
}
const scannedCache = /* @__PURE__ */ new WeakMap();
const totalSelectorsInserted = /* @__PURE__ */ new Map();
function listenForSheetChanges() {
  if (!isClient)
    return;
  const mo = new MutationObserver((entries) => {
    for (const entry of entries) {
      if (entry instanceof HTMLStyleElement && entry.sheet || entry instanceof HTMLLinkElement && entry.href.endsWith(".css")) {
        scanAllSheets();
        break;
      }
    }
  });
  mo.observe(document.head, {
    childList: true
  });
}
let lastScannedSheets = null;
function scanAllSheets() {
  if (process.env.NODE_ENV === "test")
    return;
  if (!isClient)
    return;
  const sheets = document.styleSheets || [];
  const prev = lastScannedSheets;
  const current = new Set(sheets);
  if (document.styleSheets) {
    for (const sheet2 of current) {
      sheet2 && updateSheetStyles(sheet2);
    }
    lastScannedSheets = current;
  }
  if (prev) {
    for (const sheet2 of prev) {
      if (sheet2 && !current.has(sheet2)) {
        updateSheetStyles(sheet2, true);
      }
    }
  }
}
function track(id, remove = false) {
  const next = (totalSelectorsInserted.get(id) || 0) + (remove ? -1 : 1);
  totalSelectorsInserted.set(id, next);
  return next;
}
function updateSheetStyles(sheet2, remove = false) {
  var _a, _b;
  let rules;
  try {
    rules = sheet2.cssRules;
  } catch {
    return;
  }
  const firstSelector = (_a = getTamaguiSelector(rules[0])) == null ? void 0 : _a[0];
  const lastSelector = (_b = getTamaguiSelector(rules[rules.length - 1])) == null ? void 0 : _b[0];
  const cacheKey = `${rules.length}${firstSelector}${lastSelector}`;
  const lastScanned = scannedCache.get(sheet2);
  if (!remove) {
    if (lastScanned === cacheKey) {
      return;
    }
  }
  const len = rules.length;
  let fails = 0;
  for (let i = 0; i < len; i++) {
    const rule = rules[i];
    if (!(rule instanceof CSSStyleRule))
      continue;
    const response = getTamaguiSelector(rule);
    if (!response) {
      fails++;
      if (fails > 20) {
        return;
      }
      continue;
    }
    const [identifier, cssRule] = response;
    const total = track(identifier, remove);
    if (remove) {
      if (total === 0) {
        delete allSelectors[identifier];
      }
    } else if (!(identifier in allSelectors)) {
      const isTransform = identifier.startsWith("_transform");
      const shouldInsert = isTransform ? addTransform(identifier, cssRule.cssText, cssRule) : true;
      if (shouldInsert) {
        allSelectors[identifier] = cssRule.cssText;
      }
    }
  }
  scannedCache.set(sheet2, cacheKey);
}
function getTamaguiSelector(rule) {
  if (rule instanceof CSSStyleRule) {
    const text = rule.selectorText;
    if (text[0] === "." && text[1] === "_") {
      return [text.slice(1), rule];
    }
    if (text.startsWith(":root") && text.includes("._")) {
      return [getIdentifierFromTamaguiSelector(text), rule];
    }
  } else if (rule instanceof CSSMediaRule) {
    if (rule.cssRules.length > 1)
      return null;
    return getTamaguiSelector(rule.cssRules[0]);
  }
  return null;
}
const getIdentifierFromTamaguiSelector = (selector) => selector.replace(/(:root)+\s+/, "").replace(/:[a-z]+$/, "").slice(1);
const sheet = isClient ? document.head.appendChild(document.createElement("style")).sheet : null;
function updateRules(identifier, rules) {
  if (identifier in allRules) {
    return false;
  }
  allRules[identifier] = rules.join(" ");
  if (identifier.startsWith("_transform")) {
    return addTransform(identifier, rules[0]);
  }
  return true;
}
function insertStyleRules(rulesToInsert) {
  if (!rulesToInsert.length || !sheet) {
    return;
  }
  for (const { identifier, rules } of rulesToInsert) {
    if (!shouldInsertStyleRules(identifier)) {
      continue;
    }
    allSelectors[identifier] = rules.join("\n");
    track(identifier);
    updateRules(identifier, rules);
    for (const rule of rules) {
      if (process.env.NODE_ENV === "production") {
        sheet.insertRule(rule, sheet.cssRules.length);
      } else {
        try {
          sheet.insertRule(rule, sheet.cssRules.length);
        } catch (err) {
          console.groupCollapsed(
            `Error inserting rule into CSSStyleSheet: ${String(err)}`
          );
          console.log({ rule, rulesToInsert });
          console.trace();
          console.groupEnd();
        }
      }
    }
  }
}
function shouldInsertStyleRules(identifier) {
  if (process.env.IS_STATIC === "is_static") {
    return true;
  }
  const total = totalSelectorsInserted.get(identifier);
  return total === void 0 || total < 2;
}
export {
  getAllRules,
  getAllSelectors,
  getAllTransforms,
  insertStyleRules,
  insertedTransforms,
  listenForSheetChanges,
  scanAllSheets,
  shouldInsertStyleRules,
  updateRules
};
//# sourceMappingURL=insertStyleRule.js.map
